import React, { useState } from "react";
import './BalanceSheet.css';
import { Tooltip, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts'; // Import charting components

interface Props {
  balanceSheet: BalanceSheetProps;
}

interface LineItem {
  name: string;
  value: number;
  account_id?: string;
  line_items?: LineItem[];
}

export interface BalanceSheetProps {
  id: number;
  rootfi_id: number;
  period_start: string;
  period_end: string;
  assets: LineItem[];
  liabilities: LineItem[];
  equity: LineItem[];
}

const BalanceSheet: React.FC<Props> = ({ balanceSheet }) => {
  const [activeSection, setActiveSection] = useState<string>('Assets');
  const [collapsedItems, setCollapsedItems] = useState<{ [key: string]: boolean }>({});

  // Handle toggling of line item collapse/expand
  const toggleCollapse = (itemName: string) => {
    setCollapsedItems(prevState => ({
      ...prevState,
      [itemName]: !prevState[itemName],
    }));
  };

  // Render line items recursively with collapsible functionality
  const renderLineItems = (lineItems?: LineItem[], level: number = 0) => {
    if (!lineItems || lineItems.length === 0) return null;

    return (
      <ul className="line-items">
        {lineItems.map((item, index) => (
          <li key={`${item.name}-${index}`} className="line-item" style={{ marginLeft: `${level * 20}px` }}>
            <div className="line-item-header" onClick={() => toggleCollapse(item.name)}>
              <div className="line-item-name">
                <strong>{item.name}</strong>
              </div>
              
              <div className="line-item-value">
                ${item.value.toLocaleString()}
              </div>
              
              {item.line_items && (
                <span className="collapse-toggle">
                  {collapsedItems[item.name] ? '+' : '-'}
                </span>
              )}
            </div>
            {item.account_id && <span className="account-id">(Account ID: {item.account_id})</span>}
            {item.line_items && !collapsedItems[item.name] && renderLineItems(item.line_items, level + 1)}
          </li>
        ))}
      </ul>
    );
  };

  // Render sections for assets, liabilities, and equity
  const renderSection = (title: string, data: LineItem[]) => (
    <section className={`balance-sheet-section ${activeSection === title ? 'active' : ''}`}>
      <h3 className="section-title">{title}</h3>
      <div>{renderLineItems(data)}</div>
    </section>
  );

  // Handle switching between sections (Assets, Liabilities, Equity)
  const handleTabChange = (section: string) => {
    setActiveSection(section);
  };

  // Calculate total values for high-level summaries
  const calculateTotal = (items: LineItem[]): number => {
    return items.reduce((total, item) => {
      return total + item.value + (item.line_items ? calculateTotal(item.line_items) : 0);
    }, 0);
  };

  // Prepare pie chart data
  const pieData = [
    { name: 'Assets', value: calculateTotal(balanceSheet.assets) },
    { name: 'Liabilities', value: calculateTotal(balanceSheet.liabilities) },
    { name: 'Equity', value: calculateTotal(balanceSheet.equity) },
  ];

  return (
    <div className="balance-sheet-container">
      <div className="balance-sheet-header">
        <h1 className="header-title">Balance Sheet</h1>
        <h2 className="header-subtitle">
          Period: {balanceSheet.period_start} to {balanceSheet.period_end}
        </h2>
      </div>

      {/* Quick Overview Section */}
      <div className="summary-section">
        <h3 className="summary-title">Quick Overview</h3>
        <div className="summary-cards">
          <div className="summary-card">
            <h4>Assets</h4>
            <p>${calculateTotal(balanceSheet.assets).toLocaleString()}</p>
          </div>
          <div className="summary-card">
            <h4>Liabilities</h4>
            <p>${calculateTotal(balanceSheet.liabilities).toLocaleString()}</p>
          </div>
          <div className="summary-card">
            <h4>Equity</h4>
            <p>${calculateTotal(balanceSheet.equity).toLocaleString()}</p>
          </div>
        </div>

        {/* Pie Chart */}
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              outerRadius={80}
              fill="#8884d8"
              label={({ value }) => {
                // Format the number to match the format in summary
                return `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
              }}
            >
              <Cell key="assets" fill="#82ca9d" />
              <Cell key="liabilities" fill="#ff7300" />
              <Cell key="equity" fill="#8884d8" />
            </Pie>
            <Tooltip
              formatter={(value: number) => value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Tabs for switching between sections */}
      <div className="tabs">
        <button
          className={`tab ${activeSection === 'Assets' ? 'active' : ''}`}
          onClick={() => handleTabChange('Assets')}
        >
          Assets
        </button>
        <button
          className={`tab ${activeSection === 'Liabilities' ? 'active' : ''}`}
          onClick={() => handleTabChange('Liabilities')}
        >
          Liabilities
        </button>
        <button
          className={`tab ${activeSection === 'Equity' ? 'active' : ''}`}
          onClick={() => handleTabChange('Equity')}
        >
          Equity
        </button>
      </div>

      {/* Render the active section */}
      <div className="balance-sheet-content">
        {activeSection === 'Assets' && renderSection("Assets", balanceSheet.assets)}
        {activeSection === 'Liabilities' && renderSection("Liabilities", balanceSheet.liabilities)}
        {activeSection === 'Equity' && renderSection("Equity", balanceSheet.equity)}
      </div>
    </div>
  );
};

export default BalanceSheet;
